import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Sparkles, Trash2, UsersRound } from 'lucide-react';
import type { Role, Team, User } from '@reflex/shared';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@reflex/ui/components/ui/alert-dialog';
import { CanGuard, usePermission } from '@reflex/ui/client/permissions';
import { MemberPicker } from '@/components/members/MemberPicker';
import { MemberRow } from '@/components/members/MemberRow';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { DetailPageShell } from '@/components/shared/DetailPageShell';
import { EntityDetailsCard } from '@/components/shared/EntityDetailsCard';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { NestedDetailHeader } from '@/components/shared/NestedDetailHeader';
import { useRefreshSession } from '@/hooks/auth/useSessionRefresh';
import { useUsers } from '@/hooks/auth/useUsers';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { mutateWithToast } from '@/lib/mutation-helpers';

export function OrganizationTeamDetailPage() {
  return (
    <RequireActiveOrganization title="Team">
      {(org) => <TeamDetailLoader orgId={org.id} />}
    </RequireActiveOrganization>
  );
}

function TeamDetailLoader({ orgId }: { orgId: string }) {
  const { teamId } = useParams<{ teamId: string }>();
  const id = teamId ?? '';
  const routes = useOrgRoutes();

  const teamQuery = useQuery({
    queryKey: ['teams', id],
    queryFn: () => api.getTeam(id),
    enabled: !!id,
  });

  if (!id) {
    return (
      <PageLayout>
        <PageHeader title="Team" description="Missing team id." />
      </PageLayout>
    );
  }

  return (
    <DetailPageShell
      title="Team"
      loadingDescription="Loading team details…"
      errorDescription="This team could not be loaded."
      back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
      isLoading={teamQuery.isLoading}
      isError={teamQuery.isError}
      data={teamQuery.data}
    >
      {(team) => <TeamEditor key={team.id} team={team} orgId={orgId} />}
    </DetailPageShell>
  );
}

interface TeamEditorProps {
  team: Team;
  orgId: string;
}

function TeamEditor({ team, orgId }: TeamEditorProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refreshSession = useRefreshSession();
  // Evaluated within this team, like the server's gate on these routes: a
  // maintainer of this team manages it without org-wide `teams:manage`.
  const canManage = usePermission('teams:manage', { team: team.id });
  const routes = useOrgRoutes();

  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description ?? '');

  const membersQuery = useQuery({
    queryKey: ['teams', team.id, 'members'],
    queryFn: () => api.listTeamMembers(team.id),
  });

  const orgRolesQuery = useQuery({
    queryKey: ['organizations', orgId, 'roles'],
    queryFn: () => api.listOrganizationRoles(orgId),
    enabled: !!orgId,
  });

  const usersQuery = useUsers();

  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    for (const user of usersQuery.data ?? []) map.set(user.id, user);
    return map;
  }, [usersQuery.data]);

  const memberUserIds = useMemo(
    () => new Set((membersQuery.data ?? []).map((m) => m.userId)),
    [membersQuery.data],
  );

  const selectableUsers = useMemo(
    () => (usersQuery.data ?? []).filter((u) => !memberUserIds.has(u.id)),
    [usersQuery.data, memberUserIds],
  );

  const teamScopedRoles = useMemo<Role[]>(
    () => (orgRolesQuery.data ?? []).filter((r) => r.scope === 'team'),
    [orgRolesQuery.data],
  );

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string | null; defaultRoleId?: string }) =>
      api.updateTeam(team.id, data),
    onSuccess: (next) => {
      queryClient.setQueryData(['teams', team.id], next);
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'teams'] });
      // `activeMembership.teams` carries whole team objects, names included,
      // so a rename leaves `useMyTeams` showing the old label until the
      // session is re-read.
      refreshSession();
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => api.addTeamMember(team.id, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', team.id, 'members'] });
      refreshSession();
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.removeTeamMember(team.id, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', team.id, 'members'] });
      refreshSession();
    },
  });

  const setMemberRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.setTeamMemberRole(team.id, userId, roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', team.id, 'members'] });
      refreshSession();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteTeam(team.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'teams'] });
      refreshSession();
      navigate(routes.teams);
    },
  });

  const dirty = name !== team.name || (description || null) !== (team.description ?? null);

  function handleSave() {
    const payload: { name?: string; description?: string | null } = {};
    if (name !== team.name) payload.name = name;
    if ((description || null) !== (team.description ?? null)) {
      payload.description = description.length === 0 ? null : description;
    }
    if (Object.keys(payload).length === 0) return;
    mutateWithToast(updateMutation, payload, {
      success: 'Team updated',
      error: 'Failed to update team',
    });
  }

  function handleReset() {
    setName(team.name);
    setDescription(team.description ?? '');
  }

  function handleChangeDefaultRole(nextRoleId: string) {
    if (nextRoleId === team.defaultRoleId) return;
    mutateWithToast(
      updateMutation,
      { defaultRoleId: nextRoleId },
      {
        success: 'Default role updated',
        error: 'Failed to update default role',
      },
    );
  }

  function handleChangeMemberRole(userId: string, nextRoleId: string) {
    mutateWithToast(
      setMemberRoleMutation,
      { userId, roleId: nextRoleId },
      {
        success: 'Member role updated',
        error: 'Failed to update member role',
      },
    );
  }

  const readOnly = !canManage;

  return (
    <PageLayout maxWidth="4xl">
      <NestedDetailHeader
        back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
        title={team.name}
        description={team.description ?? 'Team details and members.'}
        docs="administration/organization-teams#teams"
        icon={<UsersRound className="h-5 w-5 text-muted-foreground" />}
        iconVariant="box"
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link
                to={routes.teamModelProviders(team.id)}
                aria-label={`${team.name} model providers`}
              >
                <Sparkles className="icon-sm" />
                <span className="sm:hidden">Providers</span>
                <span className="hidden sm:inline">Model Providers</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={routes.teamSecrets(team.id)} aria-label={`${team.name} secrets`}>
                <KeyRound className="icon-sm" /> Secrets
              </Link>
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="team-detail-badges">
        <Badge variant="secondary">{team.slug}</Badge>
        <span className="text-xs text-muted-foreground">
          {membersQuery.data?.length ?? 0} member
          {membersQuery.data?.length === 1 ? '' : 's'}
        </span>
      </div>

      <EntityDetailsCard
        description="Identifying information for this team."
        data-testid="team-details-section"
        collapsible
        disabled={readOnly}
        name={{
          id: 'team-name',
          label: 'Name',
          value: name,
          onChange: setName,
          maxLength: 100,
        }}
        identifier={{
          id: 'team-slug',
          label: 'Identifier',
          value: team.slug,
          readOnly: true,
        }}
        descriptionField={{
          id: 'team-description',
          label: 'Description',
          value: description,
          onChange: setDescription,
          maxLength: 500,
        }}
        dirty={dirty}
        saving={updateMutation.isPending}
        onDiscard={handleReset}
        onSave={handleSave}
        savePermission="teams:manage"
        savePermissionTeam={team.id}
      />

      <SectionCard
        title="Default role"
        description="Role automatically assigned when someone is added to this team. Only team-scoped roles can be the default."
        data-testid="team-default-role-section"
      >
        <AsyncContent
          isLoading={orgRolesQuery.isLoading}
          isError={orgRolesQuery.isError}
          loadingMessage="Loading roles…"
          errorMessage="Failed to load roles."
        >
          {teamScopedRoles.length > 0 ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Select
                value={team.defaultRoleId ?? undefined}
                onValueChange={handleChangeDefaultRole}
                disabled={readOnly || updateMutation.isPending}
              >
                <SelectTrigger
                  className="w-full sm:w-72"
                  aria-label="Default team role"
                  data-testid="team-default-role-trigger"
                >
                  <SelectValue placeholder="Select a default role" />
                </SelectTrigger>
                <SelectContent>
                  {teamScopedRoles.map((role) => (
                    <SelectItem
                      key={role.id}
                      value={role.id}
                      data-testid={`team-default-role-option-${role.id}`}
                    >
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {team.defaultRoleId && (
                <p className="text-xs text-muted-foreground">
                  {teamScopedRoles.find((r) => r.id === team.defaultRoleId)?.description ?? null}
                </p>
              )}
            </div>
          ) : (
            <EmptyState variant="bordered" title="No team-scoped roles available" />
          )}
        </AsyncContent>
      </SectionCard>

      <SectionCard
        title="Members"
        description="People who are members of this team. Each member is granted one team-scoped role."
        data-testid="team-members-section"
        action={
          <CanGuard permission="teams:manage" team={team.id}>
            <MemberPicker
              users={selectableUsers}
              disabled={addMemberMutation.isPending || usersQuery.isLoading}
              triggerLabel="Add team member"
              ariaLabel="Add team member"
              emptyState={
                (usersQuery.data?.length ?? 0) === 0 ? (
                  <>
                    No people in this organization yet.{' '}
                    <Link
                      to={routes.members}
                      className="font-medium text-foreground underline underline-offset-2"
                    >
                      Invite teammates
                    </Link>{' '}
                    to add them here.
                  </>
                ) : (
                  <>Everyone in this organization is already on this team.</>
                )
              }
              onSelect={(userId) =>
                mutateWithToast(addMemberMutation, userId, {
                  success: 'Member added',
                  error: 'Failed to add member',
                })
              }
            />
          </CanGuard>
        }
      >
        <AsyncContent
          isLoading={membersQuery.isLoading}
          isError={membersQuery.isError}
          loadingMessage="Loading members…"
          errorMessage="Failed to load members."
        >
          {membersQuery.data && membersQuery.data.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {membersQuery.data.map((member) => {
                const user = usersById.get(member.userId);
                const removeLabel = user?.name ?? user?.login ?? member.userId;
                return (
                  <MemberRow
                    key={member.memberId}
                    user={user}
                    userIdFallback={member.userId}
                    testId={`team-member-row-${member.userId}`}
                    rightSlot={
                      <>
                        <Select
                          value={member.role?.id ?? undefined}
                          onValueChange={(next) => handleChangeMemberRole(member.userId, next)}
                          disabled={
                            readOnly ||
                            setMemberRoleMutation.isPending ||
                            teamScopedRoles.length === 0
                          }
                        >
                          <SelectTrigger
                            className="w-36 sm:w-48"
                            aria-label={`Role for ${removeLabel}`}
                            data-testid={`team-member-role-trigger-${member.userId}`}
                          >
                            <SelectValue placeholder="No role" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamScopedRoles.map((role) => (
                              <SelectItem
                                key={role.id}
                                value={role.id}
                                data-testid={`team-member-role-option-${member.userId}-${role.id}`}
                              >
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <CanGuard permission="teams:manage" team={team.id}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              mutateWithToast(removeMemberMutation, member.userId, {
                                success: 'Member removed',
                                error: 'Failed to remove member',
                              })
                            }
                            disabled={removeMemberMutation.isPending}
                            aria-label={`Remove ${removeLabel}`}
                          >
                            <Trash2 className="icon-sm" />
                          </Button>
                        </CanGuard>
                      </>
                    }
                  />
                );
              })}
            </ul>
          ) : (
            <EmptyState variant="bordered" title="No team members yet" />
          )}
        </AsyncContent>
      </SectionCard>

      <CanGuard permission="teams:manage" team={team.id}>
        <SectionCard
          title="Danger zone"
          description="Deleting a team removes its members and role bindings. This cannot be undone."
          variant="destructive"
          data-testid="team-danger-zone-section"
        >
          <div className="flex items-center justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={deleteMutation.isPending}>
                  <Trash2 className="icon-sm" /> Delete team
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this team?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {team.name} will be removed along with its {membersQuery.data?.length ?? 0}{' '}
                    member{membersQuery.data?.length === 1 ? '' : 's'} and its role bindings. This
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() =>
                      mutateWithToast(deleteMutation, undefined, {
                        success: 'Team deleted',
                        error: 'Failed to delete team',
                      })
                    }
                  >
                    Delete team
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </SectionCard>
      </CanGuard>
    </PageLayout>
  );
}
